<template>
  <div class="mb-3.5 overflow-hidden rounded bg-gray-100" data-testid="bogo-info">
    <!-- Blue Header -->
    <div class="bg-[#3D65F5] px-2 py-3.5 lg:px-3.5" data-testid="bogo-info-header">
      <h3
        class="font-inter m-0 text-xs font-bold leading-[1.15] text-white lg:text-base"
        data-testid="bogo-info-header-title"
      >
        {{ headerTitle }}
      </h3>
    </div>

    <!-- Content -->
    <div class="rounded-b border-[1.5px] bg-gray-100 p-2 lg:p-3" data-testid="bogo-info-content">
      <div class="rounded bg-white p-2 pt-3.5 lg:p-1">
        <!-- Product Cards + Description - horizontal layout on desktop -->
        <div
          class="flex flex-col items-center gap-1.5 lg:flex-row-reverse lg:items-start lg:gap-2 xl:gap-4"
          data-testid="bogo-info-product-section"
        >
          <!-- Left side: Product cards -->
          <div
            class="relative flex flex-shrink-0 items-center justify-center gap-7 lg:gap-1.5"
            data-testid="bogo-info-cards"
          >
            <!-- Buy Blanks Card -->
            <figure
              class="m-0! flex! relative h-[126px] w-[104px] flex-col items-center rounded border-[1.5px] border-gray-100 bg-white pt-0.5 text-center lg:h-[180px] lg:w-[147px] lg:border-[3px] lg:pt-1 xl:h-[193px] xl:w-[158px] xl:pt-0.5"
              data-testid="bogo-info-blanks-card"
            >
              <img
                :src="blanksImageUrl"
                alt="Blank t-shirt"
                class="h-[96px] w-auto object-contain lg:h-[132px] xl:h-[143px]"
                loading="lazy"
                data-testid="bogo-info-blanks-image"
              />
              <figcaption
                class="absolute bottom-0.5 left-0 right-0 text-[9px] font-bold leading-[2] text-gray-700 lg:bottom-1 lg:text-[13px] xl:text-sm"
                data-testid="bogo-info-blanks-caption"
              >
                Buy Blanks
              </figcaption>
            </figure>

            <!-- Plus Sign - overlapping between cards -->
            <div
              class="z-10 -mx-[23px] flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-full bg-gray-900 text-xl font-bold text-white lg:h-[38px] lg:w-[38px] lg:text-2xl xl:h-[42px] xl:w-[42px] xl:text-[28px]"
              data-testid="bogo-info-plus-sign"
            >
              +
            </div>

            <!-- Get FREE Transfers Card -->
            <figure
              class="m-0! flex! relative h-[126px] w-[104px] flex-col items-center rounded border-[1.5px] border-white bg-[#B5DEFF]/50 pt-1.5 text-center lg:h-[180px] lg:w-[147px] lg:border-[3px] lg:pt-2 xl:h-[193px] xl:w-[158px] xl:pt-2.5"
              data-testid="bogo-info-transfers-card"
            >
              <img
                :src="transfersImageUrl"
                alt="Transfer design mockup"
                class="h-[92px] w-auto object-contain lg:h-[128px] xl:h-[135px]"
                loading="lazy"
                data-testid="bogo-info-transfers-image"
              />
              <figcaption
                class="absolute bottom-0.5 left-0 right-0 text-[9px] font-bold leading-[2] text-gray-700 lg:bottom-1 lg:text-[13px] xl:text-sm"
                data-testid="bogo-info-transfers-caption"
              >
                Get FREE Transfers
              </figcaption>
            </figure>
          </div>

          <!-- Right side: Description and delivery -->
          <div
            class="flex flex-1 flex-col justify-start text-center lg:pl-2 lg:pt-2.5 lg:text-left xl:pl-3 xl:pt-2"
            data-testid="bogo-info-description"
          >
            <!-- How it works title -->
            <h4
              class="m-0 mb-1 text-base font-bold leading-6 text-gray-700 lg:text-sm lg:leading-relaxed xl:mb-0 xl:text-lg"
              data-testid="bogo-info-how-it-works-title"
            >
              How it works
            </h4>

            <p
              class="mb-2.5! lg:mb-7! xl:mb-6! px-6 text-[13px] leading-[1.4] text-gray-700 lg:px-0 lg:text-sm lg:leading-[1.86] xl:text-base xl:leading-relaxed"
              data-testid="bogo-info-how-it-works-text"
            >
              For every blank you add, get a
              <template v-if="maxFreeDimensionLabel === 'any size'">
                standard transfer of any size
              </template>
              <template v-else> standard transfer {{ maxFreeDimensionLabel }} or smaller </template>
              <strong>FREE!</strong>
            </p>

            <p
              class="text-lg font-bold text-gray-700 lg:text-xs xl:text-lg"
              data-testid="bogo-info-guarantee"
            >
              100% Satisfaction Guaranteed
            </p>
          </div>
        </div>

        <!-- Divider -->
        <div
          class="mb-2 h-px bg-gray-200 lg:mb-2.5 lg:mt-2 xl:mx-1.5"
          data-testid="bogo-info-divider"
        ></div>

        <!-- Benefits List -->
        <ul
          class="gap-x-6.5 lg:gap-y-6.5 m-0 flex list-none flex-wrap justify-center gap-y-2 p-0"
          data-testid="bogo-info-benefits"
        >
          <li class="flex items-center gap-2" data-testid="bogo-info-benefit-weeding">
            <span
              class="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-[#3D65F5]"
            >
              <AwardIcon class="h-[11px] w-[11px] text-white" />
            </span>
            <span class="text-xs leading-relaxed text-gray-600">No Weeding/Layering</span>
          </li>
          <li class="flex items-center gap-2" data-testid="bogo-info-benefit-heat-press">
            <span
              class="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-[#3D65F5]"
            >
              <StickyNoteIcon class="h-[11px] w-[11px] text-white" />
            </span>
            <span class="text-xs leading-relaxed text-gray-600">Same Heat Press</span>
          </li>
          <li class="flex items-center gap-2" data-testid="bogo-info-benefit-artist-edits">
            <span
              class="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-[#3D65F5]"
            >
              <BrushIcon class="h-[11px] w-[11px] text-white" />
            </span>
            <span class="text-xs leading-relaxed text-gray-600">Free Artist Edits</span>
          </li>
        </ul>

        <!-- Footer Disclaimer -->
        <p
          class="mb-0! xl:mt-1.5! mt-2! lg:mb-1! text-center text-xs leading-relaxed text-gray-600"
          data-testid="bogo-info-disclaimer"
        >
          <template v-if="isGuest">
            *Offer valid for logged-in customers on their first purchase in each Jiffy business line
            per account. Limit one (1) redemption per household.
            <a
              href="/signup"
              class="underline"
              data-testid="bogo-info-signup-link"
              @click="trackSignupClick"
            >
              Create an account
            </a>
            to get the discount at checkout.
          </template>
          <template v-else>
            *Offer valid for first-time purchases within each Jiffy business line per customer
            account. Limit one (1) redemption per household. Discount applied at checkout.
          </template>
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { track } from "jiffy-analytics";
import { computed } from "vue";
import { remoteImageStaticUrl } from "common/utils/remote-image";
import AwardIcon from "assets/icons/bogo-award.svg?vue";
import StickyNoteIcon from "assets/icons/bogo-sticky-note.svg?vue";
import BrushIcon from "assets/icons/brush.svg?vue";

type BogoReason = "both" | "blanks" | "dtf" | null;

type Props = {
  reason?: BogoReason;
  isGuest?: boolean;
  maxFreeDimensionLabel?: string;
};

const props = withDefaults(defineProps<Props>(), {
  reason: null,
  isGuest: false,
  maxFreeDimensionLabel: '12"x12"'
});

const trackSignupClick = () => {
  track("click_discount_signup_link", {
    current_page: "DTF001"
  });
};

const headerTitle = computed(() => {
  // Cohort C (Blanks-Only, no transfers): reason = 'dtf' (first-time DTF buyer)
  if (props.reason === "dtf") {
    return "You trust our Blanks. Now complete the look, on us.";
  }
  // Cohort A (Guest/Net New) and Cohort B (Transfers-Only): reason = 'both' or 'blanks'
  return "Get your blanks from us, and get your transfers FREE.";
});

const blanksImageUrl = computed(() =>
  remoteImageStaticUrl("gen/bogo-transfers/blanks-g300.jpg", { w: 190, fit: "clip", q: 80 })
);

const transfersImageUrl = computed(() =>
  remoteImageStaticUrl("gen/bogo-transfers/colored-sticker-full.png", {
    w: 324,
    fit: "clip",
    q: 80
  })
);
</script>
